import { Gender, Initial, kycStatusType, LicenceTagType, PaginationRequest } from "src/types/commonTypes";
import client from "../client";
import internalClient from "../internalClient";
import { EMPTY_STRING } from "src/components/constant/constant";
import { buildGraphqlFieldsFromMap, graphqlSearchUserFields } from "src/utils/graphqlFieldMaps";
import { normalizePhoneForApi } from "src/utils/phoneUtils";

export type CreateUserRequest = {
  firstName: string;
  lastName: string;
  gender: Gender;
  initial: Initial;
  email: string;
  phone: string;
  role: string;
  dob: string;
  city: string;
  country: string;
  line1: string;
  line2: string;
  postcode: string;
  state: string;
  organisationId?: string;
  userName?: string;
};

export type SaveUserPreferencesInput = {
  user_id: string;
  ndis_number: string;
  licence_tag?: LicenceTagType;
  exclude_license: boolean;
};

export const saveUserPreferences = (data: SaveUserPreferencesInput) => {
  return client({
    query: `
      mutation SaveUserPreferences ($input: UserPreferencesInput) {
        save_user_preferences(input: $input) {
          user_id
        }
      }
    `,
    variables: {
      input: {
        user_id: data.user_id,
        ndis_number: data.ndis_number,
        licence_tag: data.licence_tag ?? "PROVIDER_PAID",
        device_token: [],
        plan_manage_id: EMPTY_STRING,
        exclude_license: data.exclude_license
      }
    }
  });
};
export const createUser = (data: CreateUserRequest) => {
  return client({
    query: `
      mutation createUser ($input: CreateUserInput) {
        create_user(input: $input) {
          user_id
        }
      }
    `,
    variables: {
      input: {
        address: {
          city: data.city,
          country: data.country,
          line_1: data.line1,
          line_2: data.line2,
          post_code: data.postcode,
          state: data.state
        },
        date_of_birth: data.dob,
        email: data.email.toLowerCase(),
        first_name: data.firstName,
        gender: data.gender,
        initial: data.initial,
        last_name: data.lastName,
        phone: normalizePhoneForApi(data.phone),
        username: data.userName,
        role_name: data.role
      }
    }
  });
};

export type SearchUserRequest = {
  user_filter?: {
    is_active?: boolean | null;
    email?: string | null;
    full_name?: string | null;
    display_id?: string | null;
    organisation_id?: number | string | null;
    organisationDisplayId?: string;
    role?: string;
    roles?: string[];
    user_ids?: string[];
    owns_wallet?: boolean;
  };
  searchCriteria?: string;
  size?: number | null;
  from?: number | null;
};

export const searchUser = (data: SearchUserRequest, fieldsObj: Record<string, unknown> = graphqlSearchUserFields) => {
  const fields = buildGraphqlFieldsFromMap(fieldsObj);

  return client({
    query: `query SearchUser($input: SearchInput) {
      search_user(input: $input){
        ${fields}
      }
    }`,
    variables: {
      input: {
        filter: {
          user_filter: data.user_filter
        },
        search_criteria: data.searchCriteria,
        size: data.size,
        from: data.from
      }
    }
  });
};

// People holding any of these roles in the caller's organisation — the count only.
export const countOrganisationUsersByRoles = (roles: string[]) =>
  client(
    {
      query: `query countOrganisationUsers($input: SearchInput) {
        search_organisation_users(input: $input) {
          total_size
        }
      }`,
      variables: {
        input: {
          filter: { user_filter: { roles } },
          size: 1,
          from: 0
        }
      }
    },
    { suppressErrorToast: true }
  );

export const searchOrganisationUsers = (data: SearchUserRequest) => {
  return client({
    query: `query searchOrganisationUsers($input: SearchInput) {
      search_organisation_users(input: $input){
        total_pages
        total_size
        user_listing {
          attributes {
            address {
            city
            country
            post_code
            line_1
            line_2
            state
            }
            first_name
            last_name
          }
          current_organisation_role
          created_at
          display_id
          email
          is_active
          phone
          user_id
          username
          roles {
            value
            organisation_id
          }
        }
      }
    }`,
    variables: {
      input: {
        filter: {
          user_filter: data.user_filter
        },
        search_criteria: data.searchCriteria,
        size: data.size,
        from: data.from
      }
    }
  });
};

export const searchInternalUser = (data: SearchUserRequest) => {
  return internalClient({
    query: `query SearchUser($input: SearchInput) {
      internal_search_user(input: $input){
        total_pages
        total_size
        user_listing {
          attributes {
            first_name
            last_name
          }
          current_organisation_role
          created_at
          display_id
          email
          is_active
          phone
          user_id
          username
          roles {
            value
            organisation_id
          }
        }
      }
    }`,
    variables: {
      input: {
        filter: {
          user_filter: data.user_filter
        },
        search_criteria: data.searchCriteria,
        size: data.size,
        from: data.from
      }
    }
  });
};

export type SearchInternalOrganisationUsersRequest = SearchUserRequest & {
  organisation_id: string;
};

export const searchInternalOrganisationUsers = (data: SearchInternalOrganisationUsersRequest) => {
  return internalClient({
    query: `query SearchOrganisationUsers($input: InternalSearchOrganisationUsersInput) {
      internal_search_organisation_users(input: $input){
        total_pages
        total_size
        user_listing {
          attributes {
            first_name
            last_name
          }
          current_organisation_role
          created_at
          display_id
          email
          is_active
          phone
          user_id
          username
          roles {
            value
            organisation_id
          }
        }
      }
    }`,
    variables: {
      input: {
        organisation_id: data.organisation_id,
        filter: {
          user_filter: data.user_filter
        },
        search_criteria: data.searchCriteria,
        size: data.size,
        from: data.from
      }
    }
  });
};

export const userOrganisation = (userId: string) => {
  return internalClient({
    query: `query UserOrganisation($input: UserOrganisationsInput) {
      get_user_organisations(input: $input){
        organisation_id
        name
        display_id
        role
      }
    }`,
    variables: {
      input: {
        user_id: userId
      }
    }
  });
};

export type getUserById = {
  user_id?: string;
  display_id?: string;
};

export const getUserMfaExempt = (userId: string) => {
  return client(
    {
      query: `
      query GetUserMfaExempt($input: GetUserInput) {
        get_user(input: $input) {
          user_id
          preferences {
            mfa_exempt
          }
        }
      }
    `,
      variables: {
        input: {
          user_id: userId
        }
      }
    },
    { suppressErrorToast: true }
  );
};

export type UserPreferencesForSave = {
  ndis_number: string | null;
  device_token: string[] | null;
  plan_manage_id: string | null;
  card_verified: boolean | null;
  licence_tag: string | null;
  exclude_license: boolean | null;
  mfa_exempt: boolean | null;
};

// save_user_preferences replaces the whole record, so writes start from the stored values.
export const getUserPreferencesForSave = (userId: string) => {
  return client({
    query: `
      query GetUserPreferencesForSave($input: GetUserInput) {
        get_user(input: $input) {
          user_id
          preferences {
            ndis_number
            device_token
            plan_manage_id
            card_verified
            licence_tag
            exclude_license
            mfa_exempt
          }
        }
      }
    `,
    variables: {
      input: {
        user_id: userId
      }
    }
  });
};

export const saveUserMfaExemption = (userId: string, stored: UserPreferencesForSave | null, mfaExempt: boolean) => {
  return client({
    query: `
      mutation SaveUserMfaExemption($input: UserPreferencesInput) {
        save_user_preferences(input: $input) {
          user_id
          mfa_exempt
        }
      }
    `,
    variables: {
      input: {
        user_id: userId,
        ndis_number: stored?.ndis_number ?? EMPTY_STRING,
        device_token: stored?.device_token ?? [],
        plan_manage_id: stored?.plan_manage_id ?? EMPTY_STRING,
        // Optional fields are left out when unset; the API rejects explicit nulls.
        ...(stored?.card_verified != null && { card_verified: stored.card_verified }),
        ...(stored?.licence_tag != null && { licence_tag: stored.licence_tag }),
        ...(stored?.exclude_license != null && { exclude_license: stored.exclude_license }),
        mfa_exempt: mfaExempt
      }
    }
  });
};

export const resetUserMfa = (userId: string) => {
  return client({
    query: `
      mutation ResetUserMfa($input: ResetUserMfaInput) {
        reset_user_mfa(input: $input) {
          user_id
          success
          message
        }
      }
    `,
    variables: {
      input: {
        user_id: userId
      }
    }
  });
};

export const getUserById = (data: getUserById) => {
  return client({
    query: `
    query GetUserById($input: GetUserInput) {
      get_user(input: $input) {
        user_id
        username
        cognito_username
        email
        guardian_id
        is_active
        display_id
        created_at
        preferences {
          ndis_number
          licence_tag
          exclude_license
          mfa_exempt
        }
        attributes {
          address {
            city
            country
            post_code
            line_1
            line_2
            state
          }
          first_name
          last_name
          gender
          initial
          date_of_birth
          profile_image
        }
        security_status
        phone
        roles {
          value
          organisation_id
        }
      }    
    }`,
    variables: {
      input: data
    }
  });
};

export const getUserName = (userId: string) => {
  return client({
    query: `query GetUserName($input: GetUserInput) {
      get_user_name(input: $input) 
    }`,
    variables: {
      input: {
        user_id: userId
      }
    }
  });
};

export type UpdateUserRequest = {
  id?: number | string;
  active?: boolean;
  email?: string;
  userName?: string;
  initial?: Initial | null;
  firstName?: string;
  lastName?: string;
  dob?: string;
  phoneNumber?: string;
  gender?: Gender | null;
  role?: string;
  state?: string;
  city?: string;
  line1?: string;
  line2?: string;
  zipcode?: string;
  country?: string;
  accessCode?: string;
  fullName?: string;
  guardianId?: number | string;
  kycStatus?: kycStatusType | null;
  profileImage?: string;
};

export const updateUser = (data: UpdateUserRequest) => {
  return client({
    query: `mutation UpdateUser($input: UpdateUserInput) {
        update_user(input: $input) {
            user_id
        }
    }`,
    variables: {
      input: {
        user_id: data.id,
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        username: data.userName,
        gender: data.gender,
        phone: normalizePhoneForApi(data.phoneNumber),
        // // profile_image: data.profileImage,
        date_of_birth: data.dob,
        address: {
          line_1: data.line1,
          line_2: data.line2,
          city: data.city,
          state: data.state,
          post_code: data.zipcode,
          country: data.country
        }
        // guardian_id: data.guardianId
      }
    }
  });
};

export const getUserWallets = (userId: string) => {
  return client({
    query: `
      query GetUserWallets($input: UserQueryInput) {
        get_user_wallets(input: $input) {
          balance
          display_id 
          description 
          name 
          wallet_id 
          wallet_type
          user {
            email
            user_id
            attributes {
              last_name
              first_name
            }
          }
          group {
            group_id
            name
            group_type
          }
        }
      }
    `,
    variables: {
      input: {
        user_id: userId
      }
    }
  });
};

export const getConnectedUserWallets = (userId: string) => {
  return client({
    query: `
      query GetConnectedUserWallets($input: UserQueryInput) {
        get_connected_user_wallets(input: $input) {
          balance
          display_id 
          description 
          name 
          wallet_id 
          wallet_type
          user {
            email
            user_id
            attributes {
              last_name
              first_name
            }
          }
          group {
            group_id
            name
            group_type
          }
        }
      }
    `,
    variables: {
      input: {
        user_id: userId
      }
    }
  });
};

export type ConnectUserRequest = {
  roleName: string;
  userId: string;
};

export const connectUser = (data: ConnectUserRequest) => {
  return client({
    query: `
    mutation ConnectUserToOrganisation($input: ConnectUserToOrganisationInput) {
      connect_user_to_organisation(input: $input) {
        card_id
        created_at
        organisation_id
        relationship_id
        status
        sub_type
        type
        user_id
        value
        wallet_id
      }
    }`,
    variables: {
      input: {
        role_name: data.roleName,
        user_id: data.userId
      }
    }
  });
};

export type UpdateUserRoleRequest = {
  roleName: string;
  userId: string;
};

export const updateUserRole = (data: UpdateUserRoleRequest) => {
  return client({
    query: `
    mutation UpdateUserRole($input: UpdateUserRoleInput) {
      update_user_role(input: $input) {
        organisation_id
        relationship_id
        user_id
        value
        type
        sub_type
        status
      }
    }`,
    variables: {
      input: {
        role_name: data.roleName,
        user_id: data.userId
      }
    }
  });
};

export type SuspendUserRequest = {
  organisationId: string;
  userId: string;
};

export const suspendUser = (data: SuspendUserRequest) => {
  return client({
    query: `
    mutation SuspendUser($input: SuspendUserInput) {
      suspend_user(input: $input) {
        card_id
        created_at
        relationship_id
        organisation_id
        status
        sub_type
        type
        user_id
        value
        wallet_id
      }
    }`,
    variables: {
      input: {
        organisation_id: data.organisationId,
        user_id: data.userId
      }
    }
  });
};

export const getUserByEmail = (email: string) => {
  return client({
    query: `
    query GetUser($input: GetUserInput) {
      get_user(input: $input) {
        display_id
        email
        is_active
        security_status
        user_id
        username
        phone
        roles {
          card_id
          created_at
          organisation_id
          relationship_id
          sub_type
          status
          type
          user_id
          value
          wallet_id
        }
        attributes {
          address {
            city
            country
            post_code
            line_1
            line_2
            state
          }
          first_name
          last_name
          gender
          initial
          date_of_birth
        }
      }
    }`,
    variables: {
      input: {
        email: email
      }
    }
  });
};

export const getUserCard = (userId: string, organisationId: string) => {
  return client({
    query: `query GetUserCards($input: GetUserCardsInput) { 
      get_user_cards(input: $input) { 
        cards {
          ppan
          card_type
          card_status
          card_id
          holder_name
          user {
            email
            attributes {
              first_name
              last_name
            }
          }
          card_setting {
            card_id
            ppan
            wallet_id
            card_setting_status
            card_setting_id
            expires_at
          }
        }
      } 
    }`,
    variables: {
      input: {
        user_id: userId,
        organisation_id: organisationId
      }
    }
  });
};

const isUserNameAvailableRequest = (userName: string) => ({
  query: `
    query IsUserNameAvailable($username: String!) {
      is_username_available(username: $username)
    }
  `,
  variables: {
    username: userName
  }
});

const isEmailAvailableRequest = (email: string) => ({
  query: `
    query IsEmailAvailable($email: String!) {
      is_email_available(email: $email)
    }
  `,
  variables: {
    email
  }
});

export const checkUserNameAvailability = (userName: string) => {
  return client(isUserNameAvailableRequest(userName));
};

export const checkEmailAvailability = (email: string) => {
  return client(isEmailAvailableRequest(email));
};

export const checkUserDisplayIdExist = (displayId: string) => {
  return client({
    query: `
      query IsUserDisplayIdExist($display_id: String!) {
        is_user_display_id_exist(display_id: $display_id)
      }
    `,
    variables: {
      display_id: displayId
    }
  });
};

export type UpdateKycRequest = {
  userId: string;
  kycStatus: kycStatusType;
};

export const updateKYC = (data: UpdateKycRequest) => {
  return client({
    query: `
    mutation MyMutation($input: UpdateSecurityStatusInput) {
      update_security_status(input: $input) {
        security_id
      }
    }
    `,
    variables: {
      input: {
        object_id: data.userId,
        object_type: "USER",
        security_status: data.kycStatus,
        security_type: "FRANKIE"
      }
    }
  });
};

export const getCardSetting = (userId: string) => {
  return client({
    query: `query GetCardSettings($input: GetCardSettingsInput) {
      get_card_settings(input: $input) { 
        card_setting_id
	      card_setting_status
	      status
	      ppan
	      card_id
	      wallet_id
	      expires_at
 	      sub_type
	      user_id 
        updated_at
      } 
    }`,
    variables: {
      input: {
        user_id: userId
      }
    }
  });
};

export interface SearchCardSetting extends PaginationRequest {
  cardSettingFilter?: {
    card_id?: string;
    wallet_id?: string;
    status?: string;
    organisation_id?: string;
  };
}

export const searchCardSetting = (data: SearchCardSetting) => {
  return client({
    query: `query SearchCardSetting($input: SearchInput) {
      search_card_settings(input: $input) { 
        items {
          card_setting_id
	        card_setting_status
	        status
	        ppan
	        card_id
	        wallet_id
	        expires_at
 	        sub_type
	        user_id 
          updated_at
        }
	      total_pages
	      total_size
      } 
    }`,
    variables: {
      input: {
        filter: {
          card_setting_filter: data.cardSettingFilter
        },
        size: data.size,
        from: data.from
      }
    }
  });
};

export const getCurrentCardSetting = (cardId: string) => {
  return client({
    query: `
    query GetCurrentCardSetting($input: GetCurrentCardSettingInput) {
      get_current_card_setting(input: $input) {
        card_setting_id
        card_setting_status
        status
        sub_type
        expires_at
        card_id
        wallet_id
        user_id
        wallet {
          balance
          name
        }
        user {
          email
          attributes {
            first_name
            last_name
          }
        }
      }
    }
  `,
    variables: {
      input: {
        card_id: cardId
      }
    }
  });
};

export const deleteCardSetting = (cardId: string, walletId: string) => {
  return client({
    query: `
    mutation RemoveCardSetting($input: RemoveCardSettingInput) {
      remove_card_setting(input: $input) {
        code
        message
      }
    }`,
    variables: {
      input: {
        card_id: cardId,
        wallet_id: walletId,
        force: true
      }
    }
  });
};

export interface UserDownloadRequest {
  userFilter?: {
    is_active?: boolean | null;
    email?: string | null;
    full_name?: string | null;
    organisation_id?: number | string | null;
    role?: string;
  };
  columns?: string[];
  timezone: string;
  report_name?: string;
}

export const downloadUsers = (data: UserDownloadRequest) => {
  return client({
    query: `query download_users($input: DownloadReportInput!) {
      download_users(input: $input) {
        report_id
      }
    }`,
    variables: {
      input: {
        filter: {
          user_filter: data?.userFilter
        },
        payload: {
          filter: {
            user_filter: data?.userFilter ?? {}
          },
          from: 0,
          size: 10000
        },
        timezone: data?.timezone,
        columns: data?.columns,
        report_name: data?.report_name
      }
    }
  });
};
